import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const MAX_ATTEMPTS = 8;
const INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("polling"); // polling | success | failed | expired
  const attemptsRef = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;

        if (data.payment_status === "paid") {
          setState("success");
          await refresh();
          toast.success("Payment confirmed — you're in!");
          setTimeout(() => navigate("/watch", { replace: true }), 1200);
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("failed");
          return;
        }
        setTimeout(poll, INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setState("failed");
        toast.error(err?.response?.data?.detail || "Unable to verify payment");
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [params, navigate, refresh]);

  return (
    <div className="min-h-screen grid place-items-center bg-[#050505] text-white px-6">
      <div className="text-center max-w-md" data-testid="payment-success-card">
        {state === "polling" && (
          <>
            <Loader2 className="animate-spin mx-auto text-rose-500" size={48} />
            <h1 className="font-display text-5xl mt-8 uppercase">Confirming payment…</h1>
            <p className="mt-3 text-zinc-400">Hang tight — Stripe is verifying your purchase.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-400" size={48} />
            <h1 className="font-display text-5xl mt-8 uppercase">You&apos;re in</h1>
            <p className="mt-3 text-zinc-400">Redirecting to the live stream…</p>
          </>
        )}
        {(state === "failed" || state === "expired") && (
          <>
            <XCircle className="mx-auto text-rose-500" size={48} />
            <h1 className="font-display text-5xl mt-8 uppercase">
              {state === "expired" ? "Session Expired" : "Verification Failed"}
            </h1>
            <p className="mt-3 text-zinc-400">
              We couldn&apos;t confirm your payment. If you were charged, contact support.
            </p>
            <button
              onClick={() => navigate("/checkout")}
              className="btn-primary mt-8 px-8 py-3 text-xs"
              data-testid="payment-retry-btn"
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
